import React, { Fragment, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { GridColumn as Column, Grid, GridCellProps } from '@progress/kendo-react-grid'
import { NameDto } from 'api/Common/Types'
import { DocumentDto } from 'api/Documents/Types/Document'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import {
  AddClientBankAccount,
  ClientBankAccount,
  UpdateClientBankAccount,
} from 'api/NPayments/Types/ClientBankAccount'
import { FlexBox } from 'components/Box'
import ClientAreaAuditLogModal from 'components/ClientAreaAuditLogModal/ClientAreaAuditLogModal'
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal'
import { Modal } from 'components/CustomModal'
import { Spinner } from 'components/Spinner'
import { successToast, ToastContext } from 'components/Toast/Toast'
import { Ellipsis } from 'lucide-react'

import { createGridState, useGridState } from '../../../../../features/DataGrid'
import { useApiClient } from '../../../../../utils/api/ApiClient'
import { DocumentApiClient } from '../../../../../utils/api/documentApi'
import { NPaymentsApi } from '../../../../../utils/api/nPaymentsApi'
import { useCallbackWithForceRefresh } from '../../../../../utils/api/useCallbackWithForceRefresh'
import { useFetchOne } from '../../../../../utils/api/useFetch'
import { AuthSessionContext } from '../../../../../utils/AuthContext'
import { isFileTypeImage } from '../../../../../utils/check-file-type'
import { showMediaWindow } from '../../../../../utils/media-window'
import { getPaginationInitialState } from '../../../../../utils/paginationInitialState'
import { hasPermission } from '../../../../../utils/permissionsValidator'
import { AddEditBankAccountForm } from './AddEditBankAccountForm'
import { BankAccountsDropdownMenu } from './BankAccountsDropdownMenu'
import { ClientDetailsBankAccountsPageProps } from './types'

import styles from '../ClientDetailsDocument/ClientDetailsDocument.module.scss'

const useClientDetailsBankAccountsFetch = (props: ClientDetailsBankAccountsPageProps) => {
  const { data } = props

  const documentApiClient = useApiClient(DocumentApiClient)
  const paymentsApi = useApiClient(NPaymentsApi)

  const [documents, setDocuments] = useState<DocumentDto[]>([])
  const gridFilterState = createGridState(getPaginationInitialState(), {})

  const { callback: validationCallback } = useCallbackWithForceRefresh(
    () => paymentsApi.getBankAccountDetailsValidationOptions(),
    []
  )
  const { data: validationOptions, isLoading: isValidationLoading } =
    useFetchOne(validationCallback)

  const { callback: fetchData, forceRefresh } = useCallbackWithForceRefresh(() => {
    return paymentsApi.getClientBankAccounts(
      data.id,
      gridFilterState.pageIndex,
      gridFilterState.pageSize
    )
  }, [data.id])
  const { data: banksData, isLoading } = useFetchOne(fetchData)

  const getDocuments = useMemo(() => {
    return documents.map((x) => ({ id: x.id, name: x.originalFilename }))
  }, [documents])

  const getDocumentsBasedOnType = useCallback(async () => {
    const response = await documentApiClient.getBankAccountEligibleDocuments(data.id)
    setDocuments(response.items)
  }, [data.id]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    getDocumentsBasedOnType()
  }, [getDocumentsBasedOnType])

  return {
    validationOptions,
    getDocuments,
    getDocumentsBasedOnType,
    banksData,
    isLoading: isValidationLoading || isLoading,
    forceRefresh,
  }
}

export const ClientDetailsBankAccountsTab: React.FC<ClientDetailsBankAccountsPageProps> = (
  props
) => {
  const { data, setDocumentPreviewModal } = props

  const documentApiClient = useApiClient(DocumentApiClient)
  const paymentsApi = useApiClient(NPaymentsApi)

  const setToast = useContext(ToastContext)
  const [auth] = useContext(AuthSessionContext)
  const permissions = auth?.permissions || []

  const clientDetailsBankAccountsFetch = useClientDetailsBankAccountsFetch(props)
  const { banksData, validationOptions } = clientDetailsBankAccountsFetch
  const { getDocuments } = clientDetailsBankAccountsFetch
  const { isLoading, forceRefresh } = clientDetailsBankAccountsFetch

  const { gridState } = useGridState()
  const [deleteModalId, setDeleteModalId] = useState<string | null>(null)
  const [auditLogVisible, setAuditLogVisible] = useState<null | string>(null)
  const [addEditBankAccountModal, setAddEditBankAccountModal] = useState<null | {
    data?: ClientBankAccount
    visible: boolean
  }>(null)

  const handleAddEditBankAccountModal = (currentBankAccount: ClientBankAccount) => {
    setAddEditBankAccountModal({ data: currentBankAccount, visible: true })
  }

  const handleDeleteBankAccountModal = (currentBankAccount: ClientBankAccount) => {
    setDeleteModalId(currentBankAccount.id)
  }

  const handleAuditModalOpen = (currentBankAccount: ClientBankAccount) => {
    setAuditLogVisible(currentBankAccount.id)
  }

  const handleSubmit = async (values: AddClientBankAccount | UpdateClientBankAccount) => {
    if ('id' in values && values.id) {
      await paymentsApi.updateClientBankAccount(values.id, values)
    } else {
      await paymentsApi.addClientBankAccount(values)
    }
    setAddEditBankAccountModal(null)
    forceRefresh()
    setToast(successToast(`Bank ${'id' in values ? 'added' : 'updated'} successfully.`))
    forceRefresh()
  }

  const handleDelete = async () => {
    if (deleteModalId) {
      await paymentsApi.deleteClientBankAccount(deleteModalId)
      setDeleteModalId(null)
      setToast(successToast('Document deleted.'))
      forceRefresh()
    }
  }

  const showDocumentPreviewModal = async (document: NameDto<string>): Promise<void> => {
    const documentFile = await documentApiClient.getDocumentUrl(document.id)

    if (documentFile?.url && documentFile?.filename && isFileTypeImage(documentFile.filename)) {
      showMediaWindow(documentFile.url, documentFile.filename)
    } else {
      setDocumentPreviewModal({ id: document.id, name: document.name })
    }
  }

  if (isLoading) {
    return <Spinner />
  }

  if (!data) {
    return null
  }

  return (
    <Fragment>
      {addEditBankAccountModal && (
        <Modal
          visible={addEditBankAccountModal.visible}
          onClose={() => setAddEditBankAccountModal(null)}
        >
          <AddEditBankAccountForm
            showDocumentPreviewModal={showDocumentPreviewModal}
            currentBankAccount={addEditBankAccountModal.data}
            client={data}
            documents={getDocuments}
            validationOptions={validationOptions}
            onSubmit={handleSubmit}
            cancel={() => setAddEditBankAccountModal(null)}
          />
        </Modal>
      )}
      {auditLogVisible && (
        <ClientAreaAuditLogModal
          isVisible={!!auditLogVisible}
          onClose={() => setAuditLogVisible(null)}
          bankAccountId={auditLogVisible}
        />
      )}
      <ConfirmationModal
        contentText='Are you sure you wish to delete the bank account?'
        yesButtonText='Yes, delete'
        onYes={handleDelete}
        onCancel={() => setDeleteModalId(null)}
        visible={!!deleteModalId}
      />
      <FlexBox style={{ marginBottom: 36 }} alignItems='center' justifyContent='space-between'>
        <Button
          disabled={!hasPermission(permissions, PermissionsEnum.CanCreateClientAccounts)}
          onClick={() => setAddEditBankAccountModal({ visible: true })}
        >
          Add Bank Account
        </Button>
      </FlexBox>
      <Grid
        {...gridState}
        resizable
        selectedField='selected'
        expandField='expanded'
        data={banksData || []}
        sort={gridState.sort}
        total={banksData?.length || 0}
        take={100}
        skip={0}
        style={{ zIndex: 0 }}
      >
        <Column
          width={150}
          minResizableWidth={150}
          field='accountName'
          title='Beneficiary Name'
          className='table-cell-nonLatin-ellipsis'
        />
        <Column
          width={150}
          minResizableWidth={150}
          field='bankName'
          title='Bank Name'
          className='table-cell-nonLatin-ellipsis'
        />
        <Column
          width={120}
          minResizableWidth={120}
          field='bankAddress'
          title='Bank Address'
          className='table-cell-nonLatin-ellipsis'
        />
        <Column
          width={120}
          minResizableWidth={120}
          field='bankAccount'
          title='IBAN/Account Number'
          className='table-cell-nonLatin-ellipsis'
        />
        <Column
          width={120}
          minResizableWidth={120}
          field='bankCode'
          title='Swift'
          className='table-cell-nonLatin-ellipsis'
        />
        <Column
          width={120}
          minResizableWidth={120}
          field='bankCodeSort'
          title='Branch Number / Sort Code'
          className='table-cell-nonLatin-ellipsis'
        />
        <Column
          width={120}
          minResizableWidth={120}
          field='document'
          title='Supporting Documents'
          cell={(gridCellProps: GridCellProps) => {
            if (!gridCellProps.dataItem.document) {
              return <td />
            }
            return (
              <td style={{ color: 'var(--link)', textDecoration: 'underline', cursor: 'pointer' }}>
                <span
                  onClick={() =>
                    showDocumentPreviewModal({
                      name: gridCellProps.dataItem.document.name,
                      id: gridCellProps.dataItem.document.id,
                    })
                  }
                >
                  {gridCellProps.dataItem.document.name}
                </span>
              </td>
            )
          }}
        />
        <Column
          width={120}
          minResizableWidth={120}
          field='comment'
          title='Comment'
          className='table-cell-nonLatin-ellipsis'
        />
        <Column width={120} minResizableWidth={120} field='status.name' title='Status' />
        <Column
          title=''
          width={50}
          locked
          minResizableWidth={50}
          cell={(gridCellProps: GridCellProps) => (
            <td className='table-columns__sticky'>
              <DropdownMenu>
                <DropdownMenuTrigger
                  render={
                    <button
                      type='button'
                      className={styles.table_menu_button}
                      aria-label='More options'
                    />
                  }
                >
                  <Ellipsis />
                </DropdownMenuTrigger>
                <DropdownMenuContent align='end'>
                  <BankAccountsDropdownMenu
                    dataItem={gridCellProps.dataItem}
                    onAddEditBankAccountModal={handleAddEditBankAccountModal}
                    onDeleteBankAccountModal={handleDeleteBankAccountModal}
                    onAuditModalOpen={handleAuditModalOpen}
                    forceRefresh={forceRefresh}
                  />
                </DropdownMenuContent>
              </DropdownMenu>
            </td>
          )}
        />
      </Grid>
    </Fragment>
  )
}
